// Motor del cubo de ventas (BI → Cubo, estilo Presea — Martin, 4 oct 2026).
// Lógica pura: catálogo de campos, agregación de la tabla dinámica, "mostrar
// como" y fórmulas de cálculo. La pantalla (BiCuboVentasPage) solo pinta.
//
// Los datos llegan del backend en formato columnar con diccionario
// (`/api/bi/cubo/lineas`): D.campos[k] = nombre, D.vals[k] = valores distintos,
// D.cols[k][i] = índice del valor del renglón i; D.m[j][i] = número j del renglón i.

export const SEP = '\u001f'
const CK = '\u001e'
const MN = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
const MNL = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
const DS = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo']

/** Catálogo: todos los campos del renglón de Presea + los derivados de la fecha. */
const DEF = [
  { id: 'anio', label: 'Año', g: 'Fecha', deriva: f => f.slice(0, 4) },
  { id: 'trim', label: 'Trimestre', g: 'Fecha', deriva: f => f.slice(0, 4) + ' T' + (Math.floor((+f.slice(5, 7) - 1) / 3) + 1) },
  { id: 'mes', label: 'Mes', g: 'Fecha', deriva: f => f.slice(0, 7), lab: k => MN[+k.slice(5, 7) - 1] + ' ' + k.slice(0, 4) },
  // Mes del año SIN el año (Martin, 10 oct 2026): para comparar octubre 2024
  // con octubre 2025 poniendo Año en columnas. La clave "01"…"12" ordena solo.
  { id: 'mes_anio', label: 'Mes del año', g: 'Fecha', deriva: f => f.slice(5, 7), lab: k => MNL[+k - 1] },
  { id: 'fecha', label: 'Fecha', g: 'Fecha', lab: k => k.slice(8, 10) + '/' + k.slice(5, 7) + '/' + k.slice(0, 4) },
  { id: 'dmes', label: 'Día del mes', g: 'Fecha', deriva: f => f.slice(8, 10), lab: k => String(+k) },
  { id: 'dsem', label: 'Día de la semana', g: 'Fecha', deriva: f => String((new Date(f + 'T12:00:00Z').getUTCDay() + 6) % 7), lab: k => DS[+k] },
  { id: 'tipo_cbte', label: 'Tipo de comprobante', g: 'Comprobante' },
  { id: 'clase', label: 'Clase', g: 'Comprobante', lab: k => ({ venta: 'Venta', nota_credito: 'Nota de crédito', devolucion: 'Devolución' }[k] || k) },
  { id: 'numero', label: 'Número de comprobante', g: 'Comprobante' },
  { id: 'grupo', label: 'Grupo (empresa)', g: 'Comprobante' },
  { id: 'sucursal', label: 'Sucursal', g: 'Comprobante' },
  { id: 'lista', label: 'Lista de precios', g: 'Comprobante' },
  { id: 'vendedor', label: 'Vendedor', g: 'Comprobante' },
  { id: 'cod_cli', label: 'Código de cliente', g: 'Cliente' },
  { id: 'cliente', label: 'Cliente', g: 'Cliente' },
  { id: 'provincia', label: 'Provincia', g: 'Cliente' },
  { id: 'localidad', label: 'Localidad', g: 'Cliente' },
  { id: 'cod_art', label: 'Código de artículo', g: 'Artículo' },
  { id: 'articulo', label: 'Artículo', g: 'Artículo' },
  { id: 'rubro', label: 'Rubro', g: 'Artículo' },
  { id: 'subrubro', label: 'Subrubro', g: 'Artículo' },
  { id: 'tipo_prod', label: 'Tipo de producto', g: 'Artículo' },
  { id: 'familia', label: 'Familia', g: 'Artículo' },
  { id: 'cepa', label: 'Cepa', g: 'Artículo' },
  { id: 'bodega', label: 'Bodega', g: 'Artículo' },
  { id: 'cantidad', label: 'Cantidad', g: 'Valores', num: 'cantidad', fmt: 'num' },
  { id: 'facturado', label: 'Facturado', g: 'Valores', num: 'facturado', fmt: 'money' },
  { id: 'costo', label: 'Costo', g: 'Valores', num: 'costo', fmt: 'money' },
  { id: 'lleno', label: 'Precio lleno', g: 'Valores', num: 'lleno', fmt: 'money' },
]

/** Catálogo enlazado a los datos recibidos: cada campo sabe leer su valor del renglón i. */
export function crearCatalogo(D) {
  const fechaCol = D.campos.indexOf('fecha')
  const fv = D.vals[fechaCol], fc = D.cols[fechaCol]
  const campos = DEF.map(c => {
    const out = { ...c }
    if (c.num) { out.numIdx = D.valores.indexOf(c.num); return out }
    if (c.deriva) {
      // derivados de la fecha: se calculan una vez por fecha distinta
      const cache = fv.map(c.deriva)
      out.key = i => cache[fc[i]]
      return out
    }
    const k = D.campos.indexOf(c.id)
    const vals = D.vals[k], cols = D.cols[k]
    out.key = i => vals[cols[i]]
    out.distintosTotales = vals.length
    return out
  })
  const porId = Object.fromEntries(campos.map(c => [c.id, c]))
  return { campos, porId }
}

export const etiqueta = (cat, id, k) => (k === '' ? '(vacío)' : cat.porId[id].lab ? cat.porId[id].lab(k) : k)

export function valoresDistintos(cat, id, N) {
  const s = new Set(), f = cat.porId[id].key
  for (let i = 0; i < N; i++) s.add(f(i))
  return [...s].sort((a, b) => a.localeCompare(b, 'es', { numeric: true }))
}

export const RESUMEN = { sum: 'Suma', avg: 'Promedio', min: 'Mínimo', max: 'Máximo', count: 'Cantidad de renglones', dist: 'Cantidad distinta' }
export const COMO = { val: 'Valor', pg: '% del total general', pf: '% del total de la fila', pc: '% del total de la columna', dif: 'Diferencia con la columna anterior', difp: '% de diferencia con la columna anterior', acum: 'Acumulado en columnas' }

export const nombreValor = (cat, v) => v.nombre || (v.res === 'sum' ? cat.porId[v.campo].label : RESUMEN[v.res] + ' de ' + cat.porId[v.campo].label)

export function tipoValor(cat, v) {
  if (['pg', 'pf', 'pc', 'difp'].includes(v.como)) return 'pct'
  if (['count', 'dist'].includes(v.res)) return 'num'
  return cat.porId[v.campo].fmt || 'num'
}

/**
 * Agrega los renglones según la vista E. Acumula también los subtotales de cada nivel de fila.
 * El filtro de un campo que está en COLUMNAS no saca datos: elige qué columnas se
 * ven (Martin, 6 oct 2026: una columna usada en un cálculo puede no estar en la
 * vista). Las ocultas siguen en M para los cálculos y no suman al Total.
 */
export function agregar(D, cat, E) {
  const N = D.cols[0].length
  const enCol = new Set(E.col)
  const filtros = Object.entries(E.fil || {}).filter(([id, v]) => v && v.length && !enCol.has(id)).map(([id, v]) => [cat.porId[id].key, new Set(v)])
  const colVis = E.col.map(id => (E.fil?.[id]?.length ? new Set(E.fil[id]) : null))
  const fk = E.fila.map(id => cat.porId[id].key), ck = E.col.map(id => cat.porId[id].key)
  const vs = E.val.map(v => ({ v, c: cat.porId[v.campo] }))
  const M = new Map(), filasSet = new Map(), colsSet = new Set(), colsVis = new Set()
  let n = 0
  const acc = key => {
    let a = M.get(key)
    if (!a) { a = vs.map(x => ({ s: 0, n: 0, mn: Infinity, mx: -Infinity, set: x.v.res === 'dist' ? new Set() : null })); M.set(key, a) }
    return a
  }
  for (let i = 0; i < N; i++) {
    let ok = true
    for (const [f, set] of filtros) if (!set.has(f(i))) { ok = false; break }
    if (!ok) continue
    const rp = fk.map(f => f(i))
    const cp = ck.map(f => f(i))
    const c = cp.join(SEP)
    const visible = colVis.every((set, q) => !set || set.has(cp[q]))
    if (visible) { n++; colsVis.add(c) }
    colsSet.add(c)
    filasSet.set(rp.join(SEP), rp)
    const vals = vs.map(x => (x.c.numIdx != null ? D.m[x.c.numIdx][i] : x.c.key(i)))
    for (let L = 0; L <= rp.length; L++) {
      const pre = L + '|' + rp.slice(0, L).join(SEP)
      for (const cc of (visible ? [c, '*'] : [c])) {
        const a = acc(pre + CK + cc)
        for (let j = 0; j < vs.length; j++) {
          const x = vals[j], o = a[j]
          o.n++
          if (o.set) o.set.add(x)
          else if (typeof x === 'number') { o.s += x; if (x < o.mn) o.mn = x; if (x > o.mx) o.mx = x }
        }
      }
    }
  }
  return { M, filasSet, colsSet, colsVis, n }
}

export function resultado(o, res) {
  if (!o) return null
  if (res === 'sum') return o.s
  if (res === 'count') return o.n
  if (res === 'dist') return o.set ? o.set.size : null
  if (res === 'avg') return o.n ? o.s / o.n : null
  if (res === 'min') return o.n && isFinite(o.mn) ? o.mn : null
  if (res === 'max') return o.n && isFinite(o.mx) ? o.mx : null
  return null
}

/** Rótulo de una columna (clave compuesta) tal como se ve y se escribe en las fórmulas. */
export const etiquetaColumna = (cat, E, key) => key.split(SEP).map((k, q) => etiqueta(cat, E.col[q], k)).join(' › ')

/**
 * Arma la tabla lista para pintar: líneas (filas + subtotales), columnas y
 *  - `celda(L, partes, c, ci)`: números de cada columna visible de valores/cálculos por celda,
 *  - `calcFila(L, partes)`: los cálculos que comparan columnas ([Facturado · oct 2026] …), uno por línea.
 * `encab` / `encabFila` dicen nombre, tipo y si es cálculo de cada número, en el mismo orden.
 */
export function armarTabla(D, cat, E, { maxFilas = 1500, maxCols = 120 } = {}) {
  const A = agregar(D, cat, E)
  const nf = E.fila.length
  const sortK = (a, b) => a.localeCompare(b, 'es', { numeric: true })
  const cols = [...A.colsSet].sort(sortK)
  const visibles = cols.filter(c => A.colsVis.has(c))
  const colsV = visibles.slice(0, maxCols)
  const filas = [...A.filasSet.values()].sort((a, b) => { for (let k = 0; k < a.length; k++) { const c = sortK(a[k], b[k]); if (c) return c } return 0 })
  const lineas = []
  const subt = E.subtot && nf > 1
  for (let r = 0; r < filas.length; r++) {
    const p = filas[r]
    lineas.push({ tipo: 'fila', partes: p, L: nf })
    if (subt) {
      const sig = filas[r + 1]
      for (let L = nf - 1; L >= 1; L--) {
        if (!sig || p.slice(0, L).join(SEP) !== sig.slice(0, L).join(SEP)) lineas.push({ tipo: 'sub', partes: p.slice(0, L), L })
      }
    }
  }
  const lineasV = lineas.slice(0, maxFilas)
  const nombres = E.val.map(v => nombreValor(cat, v))
  const columnas = new Map(cols.map(c => [etiquetaColumna(cat, E, c), c]))
  const evs = (E.calcs || []).map(c => {
    try { return compilar(c.formula, nombres, columnas, { estricto: false }) } catch { const f = () => null; f.porFila = false; return f }
  })
  const cel = (L, partes, c) => A.M.get(L + '|' + partes.slice(0, L).join(SEP) + CK + c)
  const base = (L, partes, c, j) => resultado(cel(L, partes, c)?.[j], E.val[j].res)

  function mostrar(j, L, partes, c, ci) {
    const v = E.val[j]
    const x = base(L, partes, c, j)
    if (v.como === 'val' || x == null) return x
    if (v.como === 'pg') { const t = base(0, [], '*', j); return t ? x / t : null }
    if (v.como === 'pf') { const t = base(L, partes, '*', j); return t ? x / t : null }
    if (v.como === 'pc') { const t = base(0, [], c, j); return t ? x / t : null }
    if (c === '*') return v.como === 'acum' ? x : null
    if (v.como === 'dif' || v.como === 'difp') {
      if (ci <= 0) return null
      const p = base(L, partes, colsV[ci - 1], j)
      if (p == null) return null
      return v.como === 'dif' ? x - p : p ? x / p - 1 : null
    }
    if (v.como === 'acum') { let s = 0; for (let q = 0; q <= ci; q++) s += base(L, partes, colsV[q], j) || 0; return s }
    return x
  }

  const valVis = E.val.map((v, j) => j).filter(j => !E.val[j].oculto)
  const calcCelda = evs.map((ev, k) => k).filter(k => !evs[k].porFila)
  const calcFilaIdx = evs.map((ev, k) => k).filter(k => evs[k].porFila)
  const encab = [
    ...valVis.map(j => ({ nombre: nombres[j] + (E.val[j].como !== 'val' ? ' · ' + COMO[E.val[j].como] : ''), tipo: tipoValor(cat, E.val[j]), calc: false })),
    ...calcCelda.map(k => ({ nombre: E.calcs[k].nombre, tipo: E.calcs[k].fmt, calc: true })),
  ]
  const encabFila = calcFilaIdx.map(k => ({ nombre: E.calcs[k].nombre, tipo: E.calcs[k].fmt, calc: true }))
  const totCol = E.totCol !== false || !E.col.length
  const colKeys = !encab.length ? [] : E.col.length ? (totCol ? [...colsV, '*'] : colsV) : ['*']
  const lookup = (L, partes) => (n, col) => { const j = nombres.indexOf(n); return j < 0 ? null : resultado(cel(L, partes, col ?? '*')?.[j], E.val[j].res) }

  /** Números de una línea en una columna: [valores visibles..., cálculos por celda...] (los cálculos usan el valor crudo). */
  function celda(L, partes, c, ci) {
    const a = cel(L, partes, c)
    const m = Object.fromEntries(nombres.map((n, j) => [n, resultado(a?.[j], E.val[j].res)]))
    return [
      ...valVis.map(j => mostrar(j, L, partes, c, c === '*' ? -1 : ci)),
      ...calcCelda.map(k => evs[k](n => m[n] ?? null)),
    ]
  }
  /** Cálculos entre columnas de una línea (van una vez, al final). */
  const calcFila = (L, partes) => calcFilaIdx.map(k => evs[k](lookup(L, partes)))

  return {
    n: A.n, nf, cols, visibles, colsV, colKeys, filas, lineas, lineasV, celda, calcFila, encab, encabFila,
    totFila: E.totFila !== false, totCol,
    recorteFilas: lineas.length > maxFilas, recorteCols: visibles.length > maxCols,
  }
}

/**
 * Fórmula de cálculo → función. Sin eval: tokenizador + shunting-yard.
 * [Facturado] = el valor de la celda; [Facturado · oct 2026] = el de ESA columna
 * en la fila (la función queda marcada `porFila`). `columnas`: rótulo → clave.
 * Sin `estricto` (vista guardada con otro período), una columna que no está da vacío.
 */
export function compilar(txt, nombres, columnas = null, { estricto = true } = {}) {
  const toks = []
  let i = 0
  while (i < txt.length) {
    const c = txt[i]
    if (/\s/.test(c)) { i++; continue }
    if (c === '[') {
      const j = txt.indexOf(']', i)
      if (j < 0) throw new Error('Falta cerrar un corchete.')
      const n = txt.slice(i + 1, j).trim()
      i = j + 1
      if (nombres.includes(n)) { toks.push({ t: 'v', n }); continue }
      const v = nombres.find(x => n.startsWith(x + ' · '))
      if (v) {
        const rot = n.slice(v.length + 3).trim()
        if (columnas?.has(rot)) { toks.push({ t: 'v', n: v, col: columnas.get(rot) }); continue }
        if (!estricto) { toks.push({ t: 'v', n: v, col: CK }); continue }
        throw new Error('La tabla no tiene la columna «' + rot + '».')
      }
      throw new Error('La tabla no tiene el valor [' + n + ']. Agregalo a Valores o corregí el nombre.')
    }
    if (/[0-9.,]/.test(c)) {
      let j = i
      while (j < txt.length && /[0-9.,]/.test(txt[j])) j++
      const v = Number(txt.slice(i, j).replace(',', '.'))
      if (isNaN(v)) throw new Error('Número no válido.')
      toks.push({ t: 'n', v }); i = j; continue
    }
    if ('+-*/()−×÷'.includes(c)) { toks.push({ t: { '−': '-', '×': '*', '÷': '/' }[c] || c }); i++; continue }
    throw new Error('No entiendo «' + c + '». Usá valores entre corchetes, números, + - * / y paréntesis.')
  }
  const out = [], st = [], pr = { '+': 1, '-': 1, '*': 2, '/': 2 }
  let prev = null
  for (const k of toks) {
    if (k.t === 'v' || k.t === 'n') out.push(k)
    else if (k.t === '(') st.push(k)
    else if (k.t === ')') {
      while (st.length && st[st.length - 1].t !== '(') out.push(st.pop())
      if (!st.length) throw new Error('Sobra un paréntesis.')
      st.pop()
    } else {
      if (k.t === '-' && (!prev || ['+', '-', '*', '/', '('].includes(prev.t))) out.push({ t: 'n', v: 0 })
      while (st.length && st[st.length - 1].t !== '(' && pr[st[st.length - 1].t] >= pr[k.t]) out.push(st.pop())
      st.push(k)
    }
    prev = k
  }
  while (st.length) { const k = st.pop(); if (k.t === '(') throw new Error('Falta cerrar un paréntesis.'); out.push(k) }
  const ops = out.filter(k => '+-*/'.includes(k.t)).length
  if (!out.length) throw new Error('La fórmula está vacía.')
  if (out.length - ops !== ops + 1) throw new Error('La fórmula está incompleta.')
  const fn = get => {
    const p = []
    for (const k of out) {
      if (k.t === 'n') p.push(k.v)
      else if (k.t === 'v') p.push(get(k.n, k.col))
      else {
        const b = p.pop(), a = p.pop()
        if (a == null || b == null) { p.push(null); continue }
        p.push(k.t === '+' ? a + b : k.t === '-' ? a - b : k.t === '*' ? a * b : b ? a / b : null)
      }
    }
    return p[0]
  }
  fn.porFila = toks.some(k => k.col !== undefined)
  return fn
}

const f2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const f0 = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 })
const fp = new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
export const fmtEntero = n => f0.format(n)

/** Números completos como el resto del BI (coma de miles, 2 decimales en importes). */
export function fmtCelda(v, tipo) {
  if (v == null || !isFinite(v)) return ''
  if (tipo === 'pct') return fp.format(v * 100) + ' %'
  if (tipo === 'money') return f2.format(v)
  return f0.format(v)
}

/** Número para pegar en Excel en castellano (coma decimal, sin miles). */
export function numExcel(v, tipo) {
  if (v == null || !isFinite(v)) return ''
  return (tipo === 'pct' ? (v * 100).toFixed(2) : String(Math.round(v * 100) / 100)).replace('.', ',')
}

// ── Fechas variables del período (Martin, 15 oct 2026: "las variables grabadas
// deben ser variables: hoy, hoy-1, hoy+15, primer día del año, último día del
// año… con esto puedo dejar armados los reportes que quiero ver").
// Cada punta del período es una fecha FIJA ('2026-10-01') o una VARIABLE
// {base, n, unidad}: una fecha de referencia ± n días/meses/años. La vista
// guardada conserva la variable y la resuelve cada vez que se abre.
// resolverFecha es el ÚNICO lugar que convierte una variable en fecha.

/** Fechas de referencia, en el orden del selector. */
export const BASES_FECHA = [
  ['hoy', 'Hoy'],
  ['mes_ini', 'Primer día del mes'],
  ['mes_fin', 'Último día del mes'],
  ['mes_ant_ini', 'Primer día del mes anterior'],
  ['mes_ant_fin', 'Último día del mes anterior'],
  ['anio_ini', 'Primer día del año'],
  ['anio_fin', 'Último día del año'],
  ['anio_ant_ini', 'Primer día del año anterior'],
  ['anio_ant_fin', 'Último día del año anterior'],
]
export const UNIDADES_FECHA = [['d', 'días', 'día'], ['m', 'meses', 'mes'], ['a', 'años', 'año']]

/** Hoy en la Argentina (AAAA-MM-DD): pasadas las 21 h, la fecha UTC ya es mañana. */
export function hoyArgentina(ahora = new Date()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Argentina/Buenos_Aires', year: 'numeric', month: '2-digit', day: '2-digit' }).format(ahora)
}

const ymdUTC = d => d.toISOString().slice(0, 10)
const fechaUTC = (y, m, d) => new Date(Date.UTC(y, m, d, 12))
/** Suma meses sin desbordar: 31/03 − 1 mes = 28 o 29/02, no 03/03. */
function sumarMeses(d, n) {
  const y = d.getUTCFullYear(), m = d.getUTCMonth() + n
  const ultimo = fechaUTC(y, m + 1, 0).getUTCDate()
  return fechaUTC(y, m, Math.min(d.getUTCDate(), ultimo))
}

/** ¿Es una fecha variable (y no una fija)? */
export const esFechaVariable = v => !!v && typeof v === 'object' && BASES_FECHA.some(([b]) => b === v.base)

/** La fecha (AAAA-MM-DD) de una punta del período: la fija tal cual, la variable resuelta contra `hoy`. */
export function resolverFecha(v, hoy = hoyArgentina()) {
  if (!esFechaVariable(v)) return typeof v === 'string' ? v : ''
  const h = new Date(hoy + 'T12:00:00Z')
  const y = h.getUTCFullYear(), m = h.getUTCMonth()
  const base = {
    hoy: h,
    mes_ini: fechaUTC(y, m, 1), mes_fin: fechaUTC(y, m + 1, 0),
    mes_ant_ini: fechaUTC(y, m - 1, 1), mes_ant_fin: fechaUTC(y, m, 0),
    anio_ini: fechaUTC(y, 0, 1), anio_fin: fechaUTC(y, 11, 31),
    anio_ant_ini: fechaUTC(y - 1, 0, 1), anio_ant_fin: fechaUTC(y - 1, 11, 31),
  }[v.base]
  const n = Math.trunc(Number(v.n) || 0)
  const d = v.unidad === 'm' ? sumarMeses(base, n)
    : v.unidad === 'a' ? sumarMeses(base, n * 12)
      : fechaUTC(base.getUTCFullYear(), base.getUTCMonth(), base.getUTCDate() + n)
  return ymdUTC(d)
}

/** Cómo se lee una variable: «Hoy − 1 día», «Primer día del año», «Hoy − 1 año». */
export function etiquetaFechaVariable(v) {
  if (!esFechaVariable(v)) return ''
  const base = BASES_FECHA.find(([b]) => b === v.base)[1]
  const n = Math.trunc(Number(v.n) || 0)
  if (!n) return base
  const [, plural, singular] = UNIDADES_FECHA.find(([u]) => u === (v.unidad || 'd')) || UNIDADES_FECHA[0]
  return `${base} ${n > 0 ? '+' : '−'} ${Math.abs(n)} ${Math.abs(n) === 1 ? singular : plural}`
}

/** Atajos del período: son variables, así una vista guardada desde un atajo se mueve con el calendario. */
export const ATAJOS_PERIODO = [
  ['Este mes', { base: 'mes_ini' }, { base: 'hoy' }],
  ['Mes anterior', { base: 'mes_ant_ini' }, { base: 'mes_ant_fin' }],
  ['Últimos 3 meses', { base: 'mes_ini', n: -2, unidad: 'm' }, { base: 'hoy' }],
  ['Este año', { base: 'anio_ini' }, { base: 'hoy' }],
  ['Año anterior', { base: 'anio_ant_ini' }, { base: 'anio_ant_fin' }],
]
